import { useState, useMemo } from "react";
import { X, Loader2, Plus, Check } from "lucide-react";
import { toast } from "sonner";

export default function CreateCompanyDialog({ open, onClose, catalog, onSubmit, onCreated }) {
    const [step, setStep] = useState(1); // 1=info, 2=trades
    const [f, setF] = useState({
        name: "",
        cvr: "",
        address: "",
        postal_code: "",
        city: "",
        contact_person: "",
        contact_email: "",
        contact_phone: "",
        plan: "",
        subscription_status: "none",
    });
    const [selected, setSelected] = useState([]);
    const [primary, setPrimary] = useState("");
    const [active, setActive] = useState(true);
    const [seedDefaults, setSeedDefaults] = useState(true);
    const [saving, setSaving] = useState(false);

    const canSaveStep1 = f.name.trim().length >= 2;
    const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

    const toggleTrade = (id) => {
        setSelected((prev) => {
            const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
            if (primary && !next.includes(primary)) setPrimary(next[0] || "");
            else if (!primary && next.length === 1) setPrimary(next[0]);
            return next;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSubmit({
                ...f,
                name: f.name.trim(),
                enabled_trades: selected,
                primary_trade: primary || null,
                active,
                seed_default_categories: seedDefaults,
            });
            onCreated?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke oprette virksomhed");
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="kunde-create-dialog">
            <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-slate-900">Opret ny kundevirksomhed</h2>
                        <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="kunde-create-close">
                            <X className="w-4 h-4 text-slate-500" />
                        </button>
                    </div>
                    <div className="flex items-center gap-2 mt-3 text-xs">
                        <StepPill n={1} label="Firma & kontakt" active={step === 1} done={step > 1} />
                        <div className="h-px flex-1 bg-slate-200" />
                        <StepPill n={2} label="Fagområder" active={step === 2} />
                    </div>
                </div>
                <div className="p-6 max-h-[70vh] overflow-y-auto">
                    {step === 1 && (
                        <div className="space-y-5">
                            <FormSection title="VIRKSOMHED">
                                <Field label="Virksomhedsnavn" required>
                                    <input value={f.name} onChange={set("name")} placeholder="Fx MyServiceHelper Pilot"
                                           data-testid="kunde-create-name"
                                           className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" />
                                </Field>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="CVR">
                                        <input value={f.cvr} onChange={set("cvr")} placeholder="12345678"
                                               data-testid="kunde-create-cvr"
                                               className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-mono" />
                                    </Field>
                                    <Field label="Adresse">
                                        <input value={f.address} onChange={set("address")} placeholder="Vejnavn 1"
                                               data-testid="kunde-create-address"
                                               className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" />
                                    </Field>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Postnummer">
                                        <input value={f.postal_code} onChange={set("postal_code")} placeholder="5000"
                                               data-testid="kunde-create-postal"
                                               className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" />
                                    </Field>
                                    <Field label="By">
                                        <input value={f.city} onChange={set("city")} placeholder="Odense C"
                                               data-testid="kunde-create-city"
                                               className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" />
                                    </Field>
                                </div>
                            </FormSection>

                            <FormSection title="PRIMÆR KONTAKT">
                                <Field label="Kontaktperson">
                                    <input value={f.contact_person} onChange={set("contact_person")} placeholder="Fornavn Efternavn"
                                           data-testid="kunde-create-contact-person"
                                           className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" />
                                </Field>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Email">
                                        <input value={f.contact_email} onChange={set("contact_email")} type="email" placeholder="kontakt@firma.dk"
                                               data-testid="kunde-create-contact-email"
                                               className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" />
                                    </Field>
                                    <Field label="Telefonnummer">
                                        <input value={f.contact_phone} onChange={set("contact_phone")} placeholder="+45 60 12 34 56"
                                               data-testid="kunde-create-contact-phone"
                                               className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" />
                                    </Field>
                                </div>
                            </FormSection>

                            <FormSection title="ABONNEMENT OG STATUS">
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Plan">
                                        <input value={f.plan} onChange={set("plan")} placeholder="Fx starter, pro, enterprise"
                                               data-testid="kunde-create-plan"
                                               className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" />
                                    </Field>
                                    <Field label="Status">
                                        <select value={f.subscription_status} onChange={set("subscription_status")}
                                                data-testid="kunde-create-sub-status"
                                                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm">
                                            <option value="none">Ingen</option>
                                            <option value="trialing">Prøveperiode</option>
                                            <option value="active">Aktiv</option>
                                            <option value="grandfathered">Grandfathered</option>
                                            <option value="past_due">Forfalden</option>
                                            <option value="canceled">Opsagt</option>
                                        </select>
                                    </Field>
                                </div>
                                <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 mt-1">
                                    <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)}
                                           data-testid="kunde-create-active"
                                           className="rounded border-slate-300" />
                                    Aktivér virksomhed med det samme
                                </label>
                            </FormSection>
                        </div>
                    )}
                    {step === 2 && (
                        <div className="space-y-5">
                            <FormSection title="AKTIVE FAGOMRÅDER">
                                <p className="text-xs text-slate-500 mb-2">Voice Intake accepterer kun opgaver inden for de valgte fagområder.</p>
                                <div className="flex flex-wrap gap-2">
                                    {catalog.map((t) => {
                                        const on = selected.includes(t.id);
                                        return (
                                            <button
                                                key={t.id}
                                                type="button"
                                                onClick={() => toggleTrade(t.id)}
                                                data-testid={`kunde-create-trade-${t.id}`}
                                                className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border transition ${
                                                    on
                                                        ? "bg-blue-600 text-white border-blue-600"
                                                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                                                }`}
                                            >
                                                {on ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                                                {t.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            </FormSection>
                            {selected.length > 0 && (
                                <FormSection title="PRIMÆRT FAGOMRÅDE">
                                    <select value={primary} onChange={(e) => setPrimary(e.target.value)}
                                            data-testid="kunde-create-primary"
                                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm">
                                        <option value="">Ingen primær</option>
                                        {catalog.filter((t) => selected.includes(t.id)).map((t) => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </select>
                                </FormSection>
                            )}
                            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                                <input type="checkbox" checked={seedDefaults} onChange={(e) => setSeedDefaults(e.target.checked)}
                                       data-testid="kunde-create-seed-defaults"
                                       className="rounded border-slate-300" />
                                Autoudfyld standard-opgavetyper for hvert fagområde
                            </label>
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 border-t border-slate-200 flex justify-between gap-2 bg-slate-50">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800" data-testid="kunde-create-cancel">Annullér</button>
                    <div className="flex gap-2">
                        {step === 2 && (
                            <button onClick={() => setStep(1)}
                                    data-testid="kunde-create-back"
                                    className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">
                                Tilbage
                            </button>
                        )}
                        {step === 1 ? (
                            <button
                                onClick={() => setStep(2)}
                                disabled={!canSaveStep1}
                                data-testid="kunde-create-next"
                                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-xl">
                                Videre →
                            </button>
                        ) : (
                            <button onClick={handleSave} disabled={saving}
                                    data-testid="kunde-create-save"
                                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-xl">
                                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                                Opret virksomhed
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function StepPill({ n, label, active, done }) {
    return (
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${active ? "bg-blue-50 border-blue-200 text-blue-700" : done ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-white border-slate-200 text-slate-500"}`}>
            <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold ${active ? "bg-blue-600 text-white" : done ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600"}`}>{done ? "✓" : n}</span>
            {label}
        </div>
    );
}

function FormSection({ title, children }) {
    return (
        <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">{title}</p>
            <div className="space-y-3">{children}</div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
                {label}{required && <span className="text-rose-600"> *</span>}
            </label>
            {children}
        </div>
    );
}
